import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  canManageStandardsRelease,
  getStandardsRelease,
  isImmutableReleaseField,
  publishWithSupersession,
} from '../../lib/services/standardsReleases'

export function ReleaseDetailPage() {
  const { releaseId } = useParams()
  const { viewerContext } = useAuth()
  const canManage = canManageStandardsRelease(viewerContext?.is_superadmin === true)
  const [approverName, setApproverName] = useState('')
  const [isActing, setIsActing] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['standards-release', releaseId],
    queryFn: () => getStandardsRelease(releaseId!),
    enabled: canManage && !!releaseId,
  })

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Standards release management is restricted to Superadmin role.
      </div>
    )
  }

  if (isLoading) return <div className="p-10 text-sm text-slate-500">Loading release detail...</div>
  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load release</p>
        <p className="text-sm">{(error as Error | undefined)?.message || 'Release not found'}</p>
      </div>
    )
  }

  const release = data.release
  const metadataRows = [
    ['release_id', release.release_id],
    ['framework_id', release.framework_id],
    ['body', release.body],
    ['version', release.version],
    ['effective_date', release.effective_date],
    ['status', release.status],
  ]
  const canPublish = release.status !== 'active' && release.status !== 'retired'

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{release.release_id}</h1>
        <p className="mt-1 text-sm text-slate-600">Version/provenance metadata shown below is immutable and read-only.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Release Metadata (Immutable)</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {metadataRows.map(([key, value]) => (
            <label key={key} className="text-sm">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{key}</span>
              <input className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" value={value} readOnly={isImmutableReleaseField(key)} />
            </label>
          ))}
        </div>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Provenance (Read-Only)</h2>
        <pre className="mt-3 overflow-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">
          {JSON.stringify(data.provenance || {}, null, 2)}
        </pre>
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Publish/Supersession</h2>
        <p className="mt-1 text-xs text-slate-500">Publish action supersedes the current active release for the same standards body.</p>
        <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center">
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm md:w-72"
            placeholder="Approver name"
            value={approverName}
            onChange={(e) => setApproverName(e.target.value)}
          />
          <button
            className="btn-primary text-sm"
            disabled={isActing || !canPublish || !approverName.trim()}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                await publishWithSupersession(release, approverName.trim())
                setActionSuccess('Release published and supersession processed.')
                await refetch()
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            Publish (Supersede Active)
          </button>
        </div>
        {!canPublish && <p className="mt-2 text-xs text-slate-500">Publish action unavailable for active/retired releases.</p>}
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
      <Link to="/superadmin/releases" className="inline-block text-sm underline">
        Back to releases
      </Link>
    </div>
  )
}
